import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { TeamLogo } from '@/components/TeamLogo';
import { authManager } from '@/services/authManager';

interface MatchResult {
  matchId: string;
  homeScore: number;
  awayScore: number;
  result: 'WIN' | 'DRAW' | 'LOSS';
}

interface TeamAnalysis {
  team: {
    id: string;
    name: string;
    points: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    logoPath?: string;
    logoUrl?: string;
  };
  position: number;
  matchesPlayed: number;
  goalDifference: number;
  remainingMatches: Match[];
}

interface QualificationScenario {
  id: string;
  description: string;
  probability: number;
  requiredResults: MatchResult[];
  dependsOn: string[];
  scenarioType: 'QUALIFICATION' | 'ELIMINATION' | 'CHAMPIONSHIP';
}

interface ChampionshipScenario {
  id: string;
  description: string;
  probability: number;
  requiredResults: MatchResult[];
  pointsNeeded: number;
  scenarioType: 'CHAMPIONSHIP' | 'PLAYOFF_QUALIFICATION';
}

interface MagicNumber {
  type: 'CHAMPIONSHIP' | 'PLAYOFF' | 'ELIMINATION';
  number: number;
  description: string;
}

interface Match {
  id: string;
  homeTeam: string;
  awayTeam: string;
  dateTime?: string;
  location?: string;
  status: string;
  homeScore?: number;
  awayScore?: number;
}

interface TournamentAnalysis {
  tournament: {
    id: string;
    name: string;
    description?: string;
  };
  teamAnalysis: TeamAnalysis;
  finalsScenarios: QualificationScenario[];
  eliminationScenarios: QualificationScenario[];
  keyMatches: Match[];
  lastUpdated: string;
}

interface LeagueAnalysis {
  league: {
    id: string;
    name: string;
    description?: string;
  };
  teamAnalysis: TeamAnalysis;
  standings: TeamAnalysis[];
  championshipScenarios: ChampionshipScenario[];
  playoffScenarios: QualificationScenario[];
  magicNumbers: MagicNumber[];
  keyMatches: Match[];
  lastUpdated: string;
}

interface AnalysisDashboardProps {
  playerId: string;
}

interface PlayerAnalysisDashboardResponse {
  tournaments: TournamentAnalysis[];
  leagues: LeagueAnalysis[];
  lastUpdated: string;
}

const AnalysisDashboard: React.FC<AnalysisDashboardProps> = ({ playerId }) => {
  const [tournamentAnalyses, setTournamentAnalyses] = useState<TournamentAnalysis[]>([]);
  const [leagueAnalyses, setLeagueAnalyses] = useState<LeagueAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<TournamentAnalysis | LeagueAnalysis | null>(null);

  useEffect(() => {
    const loadAnalysisData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await authManager.getAccessToken();
        const response = await fetch(`/api/v1/analysis/player/${playerId}/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load analysis data');
        }

        const data: PlayerAnalysisDashboardResponse = await response.json();
        setTournamentAnalyses(data.tournaments || []);
        setLeagueAnalyses(data.leagues || []);
      } catch (err) {
        console.error('Error loading analysis:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    if (playerId) {
      loadAnalysisData();
    }
  }, [playerId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon />
        Analysis Dashboard
      </Typography>
      
      {tournamentAnalyses.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon />
            Tournament Analysis
          </Typography>
          {tournamentAnalyses.map(analysis => (
            <TournamentAnalysisCard 
              key={analysis.tournament.id}
              analysis={analysis}
              onViewDetails={() => setSelectedAnalysis(analysis)}
            />
          ))}
        </Box>
      )}
      
      {leagueAnalyses.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon />
            League Analysis
          </Typography>
          {leagueAnalyses.map(analysis => (
            <LeagueAnalysisCard 
              key={analysis.league.id}
              analysis={analysis}
              onViewDetails={() => setSelectedAnalysis(analysis)}
            />
          ))}
        </Box>
      )}

      {tournamentAnalyses.length === 0 && leagueAnalyses.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No analysis data available. Make sure your teams have upcoming matches and completed games with scores.
        </Alert>
      )}

      {/* Details Dialog */}
      <Dialog 
        open={!!selectedAnalysis} 
        onClose={() => setSelectedAnalysis(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon />
            {selectedAnalysis && 'tournament' in selectedAnalysis
              ? `${selectedAnalysis.tournament.name} - Detailed Analysis`
              : selectedAnalysis && 'league' in selectedAnalysis
              ? `${selectedAnalysis.league.name} - Detailed Analysis`
              : 'Analysis Details'
            }
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAnalysis && (
            <AnalysisDetails analysis={selectedAnalysis} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAnalysis(null)} startIcon={<CloseIcon />}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface TournamentAnalysisCardProps {
  analysis: TournamentAnalysis;
  onViewDetails: () => void;
}

const TournamentAnalysisCard: React.FC<TournamentAnalysisCardProps> = ({ 
  analysis,
  onViewDetails 
}) => {
  const { tournament, teamAnalysis, finalsScenarios, keyMatches } = analysis;
  
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TeamLogo 
            teamId={teamAnalysis.team.id} 
            teamName={teamAnalysis.team.name} 
            size={48}
            logoPath={teamAnalysis.team.logoPath}
            logoUrl={teamAnalysis.team.logoUrl}
          />
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            {tournament.name}
          </Typography>
          <Chip 
            label={`${teamAnalysis.team.points} pts`}
            color="primary"
            size="small"
          />
        </Box>
        
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">
                Matches Played
              </Typography>
              <Typography variant="h6">
                {teamAnalysis.matchesPlayed}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">
                Goal Difference
              </Typography>
              <Typography variant="h6" color={teamAnalysis.goalDifference >= 0 ? 'success.main' : 'error.main'}>
                {teamAnalysis.goalDifference > 0 ? '+' : ''}{teamAnalysis.goalDifference}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">
                Remaining Matches
              </Typography>
              <Typography variant="h6">
                {teamAnalysis.remainingMatches.length}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        {finalsScenarios.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Finals Qualification Scenarios:
            </Typography>
            
            {finalsScenarios.slice(0, 3).map((scenario) => (
              <Box key={scenario.id} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                {scenario.probability > 0.7 ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : scenario.probability > 0.3 ? (
                  <WarningIcon color="warning" fontSize="small" />
                ) : (
                  <CancelIcon color="error" fontSize="small" />
                )}
                <Typography variant="body2">
                  {scenario.description} ({Math.round(scenario.probability * 100)}%)
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        
        {keyMatches.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Key Match: {keyMatches[0].homeTeam} vs {keyMatches[0].awayTeam}
              {keyMatches[0].dateTime && (
                <> - {format(new Date(keyMatches[0].dateTime), 'MMM d, h:mm a')}</>
              )}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="small" onClick={onViewDetails} startIcon={<VisibilityIcon />}>
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

interface LeagueAnalysisCardProps {
  analysis: LeagueAnalysis;
  onViewDetails: () => void;
}

const LeagueAnalysisCard: React.FC<LeagueAnalysisCardProps> = ({ 
  analysis,
  onViewDetails 
}) => {
  const { league, teamAnalysis, standings, championshipScenarios, magicNumbers } = analysis;
  const teamPosition = standings.findIndex(s => s.team.id === teamAnalysis.team.id) + 1;
  
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TeamLogo 
            teamId={teamAnalysis.team.id} 
            teamName={teamAnalysis.team.name} 
            size={48}
            logoPath={teamAnalysis.team.logoPath}
            logoUrl={teamAnalysis.team.logoUrl}
          />
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            {league.name}
          </Typography>
          <Chip 
            label={`${teamPosition}${getOrdinalSuffix(teamPosition)} place`}
            color={teamPosition <= 3 ? 'success' : 'default'}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Standings (Top 5):
          </Typography>
          {standings.slice(0, 5).map((standing, index) => (
            <Box 
              key={standing.team.id} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.5,
                bgcolor: standing.team.id === teamAnalysis.team.id ? 'primary.light' : 'transparent',
                px: 1,
                borderRadius: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TeamLogo 
                  teamId={standing.team.id} 
                  teamName={standing.team.name} 
                  size={32}
                  logoPath={standing.team.logoPath}
                  logoUrl={standing.team.logoUrl}
                />
                <Typography variant="body2">
                  {index + 1}. {standing.team.name}
                </Typography>
              </Box>
              <Typography variant="body2">
                {standing.team.points} pts ({standing.team.wins}-{standing.team.draws}-{standing.team.losses})
              </Typography>
            </Box>
          ))}
        </Box>

        {magicNumbers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Magic Numbers:
            </Typography>
            {magicNumbers.map((magic, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                {magic.description}
              </Typography>
            ))}
          </Box>
        )}

        {championshipScenarios.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Championship Scenarios:
            </Typography>
            {championshipScenarios.slice(0, 2).map((scenario) => (
              <Typography key={scenario.id} variant="body2" sx={{ mb: 0.5 }}>
                • {scenario.description} ({Math.round(scenario.probability * 100)}%)
              </Typography>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="small" onClick={onViewDetails} startIcon={<VisibilityIcon />}>
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

interface AnalysisDetailsProps {
  analysis: TournamentAnalysis | LeagueAnalysis;
}

const AnalysisDetails: React.FC<AnalysisDetailsProps> = ({ analysis }) => {
  if ('tournament' in analysis) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Tournament Details</Typography>
        <Typography variant="body2" paragraph>
          {analysis.tournament.description || 'No description available'}
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom>All Scenarios:</Typography>
        {[...analysis.finalsScenarios, ...analysis.eliminationScenarios].map((scenario) => (
          <Box key={scenario.id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              {scenario.description}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Probability: {Math.round(scenario.probability * 100)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Type: {scenario.scenarioType}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  } else {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>League Details</Typography>
        <Typography variant="body2" paragraph>
          {analysis.league.description || 'No description available'}
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom>Full Standings:</Typography>
        {analysis.standings.map((standing, index) => (
          <Box key={standing.team.id} sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            px: 2,
            bgcolor: standing.team.id === analysis.teamAnalysis.team.id ? 'primary.light' : 'transparent',
            borderRadius: 1,
            mb: 0.5
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <TeamLogo 
                teamId={standing.team.id} 
                teamName={standing.team.name} 
                size={32}
                logoPath={standing.team.logoPath}
                logoUrl={standing.team.logoUrl}
              />
              <Typography variant="body2">
                {index + 1}. {standing.team.name}
              </Typography>
            </Box>
            <Typography variant="body2">
              {standing.team.points} pts | GD: {standing.goalDifference}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
};

function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export default AnalysisDashboard;
